import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { BadgeCheck, FileText, Mail, ExternalLink, UserCheck } from 'lucide-react';

const STATUSES = ['pending', 'verified', 'rejected', 'all'];

export function ClaimsQueue() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState({ claims: [], counts: {} });
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    api.get(`/admin/claims?status=${status}`).then(r => setData(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const note = action === 'reject' ? (window.prompt('Reason for rejection (sent to the claimant):') ?? null) : '';
    if (note === null) return;
    setBusy(id);
    try { await api.put(`/admin/claims/${id}`, { action, note }); toast.success(action === 'approve' ? 'Claim approved — owner verified' : 'Claim rejected'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  const openDoc = async (id) => {
    try {
      const r = await api.get(`/admin/claims/${id}/document`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Could not open document'); }
  };

  return (
    <div className="glass-panel p-4 space-y-3" data-testid="claims-queue">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-brand-orange" strokeWidth={1.5} />
          <span className="font-display text-base font-extrabold text-ink">Profile Claims</span>
          <span className="font-body text-[10px] px-1.5 py-0.5 rounded-full bg-brand-orange/10 text-brand-orange font-semibold" data-testid="claims-pending-count">{data.counts.pending || 0} pending</span>
        </div>
        <div className="flex gap-1">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`claims-filter-${s}`}
              className={`px-2.5 py-1 rounded-full font-body text-[11px] capitalize transition-colors ${status === s ? 'bg-ink text-white' : 'text-ink/50 hover:bg-ink/5'}`}>{s}{data.counts[s] ? ` (${data.counts[s]})` : ''}</button>
          ))}
        </div>
      </div>
      {data.claims.length === 0 ? (
        <p className="font-body text-xs text-ink/40 py-4 text-center" data-testid="claims-empty">No {status === 'all' ? '' : status} claims.</p>
      ) : data.claims.map(c => (
        <div key={c.id} className="solid-card p-3 flex items-center gap-3 flex-wrap" data-testid={`claim-item-${c.id}`}>
          <div className="flex-1 min-w-[200px]">
            <p className="font-body text-sm font-semibold text-ink">{c.company_name}</p>
            <p className="font-body text-[11px] text-ink/50">{c.postcode} · {c.claimant_name || c.claimant_email} · <span className="text-ink/70">{c.claimant_email}</span></p>
            <p className="font-body text-[10px] text-ink/40 mt-0.5 flex items-center gap-1">
              {c.verification_method === 'document' ? <FileText className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
              {c.verification_method === 'document' ? `Document: ${c.document_name || '—'}` : 'Email code confirmed'} · {new Date(c.created_at).toLocaleString()}
              {c.admin_note && <span className="ml-1 italic">· {c.admin_note}</span>}
            </p>
          </div>
          <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${c.verification_status === 'verified' ? 'bg-status-green/10 text-status-green' : c.verification_status === 'rejected' ? 'bg-status-red/10 text-status-red' : 'bg-status-amber/10 text-status-amber'}`}>{c.verification_status}</span>
          <div className="flex items-center gap-1.5">
            <a href={`/business/${c.business_id}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-[6px] hover:bg-ink/5 text-ink/50" title="Open profile" data-testid={`claim-open-${c.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>
            {c.document_path && <Button size="sm" variant="outline" onClick={() => openDoc(c.id)} className="h-7 font-body text-[11px] rounded-full" data-testid={`claim-doc-${c.id}`}>View doc</Button>}
            {c.verification_status === 'pending' && (
              <>
                <Button size="sm" onClick={() => act(c.id, 'approve')} disabled={busy === c.id} className="h-7 font-body text-[11px] rounded-full bg-status-green hover:bg-status-green/90" data-testid={`claim-approve-${c.id}`}><BadgeCheck className="w-3 h-3 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(c.id, 'reject')} disabled={busy === c.id} className="h-7 font-body text-[11px] rounded-full text-status-red border-status-red/30 hover:bg-status-red/5" data-testid={`claim-reject-${c.id}`}>Reject</Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
